import * as React from "react";
import { SortingOption } from "../../constants/SortingOption";
import BlockKitStore from "../../stores/BlockKitStore";
import { inject, observer } from "mobx-react";
import { Button, Col, Container, FloatingLabel, Form, InputGroup, Row } from "react-bootstrap";
import { useRef } from "react";
import { CategoryJsonModel, LicenseJsonModel } from "../../models/BlockTransportSystemJsonModel";
import { PolarProductTypes } from "../../constants/PolarProductTypes";
import { useTranslation } from "react-i18next";
import i18n from "../../../shared/internationalization/I18nSetting";
import { getBlockTypeConfigs } from "../../constants/BlockTypeConfig";

interface IBlockFilterBoxProps {
  blockKitStore?: BlockKitStore;
  isLoading: boolean;
  onClickLoading: () => void;
}

const BlockFilterBox: React.FC<IBlockFilterBoxProps> = inject("blockKitStore")(observer(({
  blockKitStore,
  isLoading,
  onClickLoading
}) => {
  const inputSearchWord = useRef<HTMLInputElement>(null);
  const {
    textAlignTypesOptions,
    tagsOptions,
    allLicenses,
    allCategories,
    allBlocks,
    layouts
  } = blockKitStore ?? {};
  const { t } = useTranslation();

  return (
    <Container fluid>
      <Row className="g-2 mb-2">
        <Col md>
          <FloatingLabel label={t("category")}>
            <Form.Select
              onChange={(e) => blockKitStore?.setCategoryId(e.target.value)}
            >
              <option value={"all"} key={"all"}>all</option>
              {allCategories?.map((category: CategoryJsonModel) =>
                <option value={category.id} key={category.id}>
                  {`${category.lang.find(c => c.key === i18n.language)?.name || category?.name} [${category?.dedicatedProducts && (category.dedicatedProducts.length > 10
                    ? [...category.dedicatedProducts.slice(0, 10), "..."].join(", ")
                    : category.dedicatedProducts.join(", "))}]`}
                </option>
              )}
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col md>
          <FloatingLabel label={t("license")}>
            <Form.Select
              onChange={(e) => blockKitStore?.setLicenseId(e.target.value)}
            >
              <option value={"all"} key={"all"}>{"all"}</option>
              <option value={"none"} key={"none"}>{t("license-none")}</option>
              {allLicenses?.map((license: LicenseJsonModel) =>
                <option
                  style={{ background: allBlocks?.some(b => b.data.licenseId === license.id) ? "#fff" : "red" }}
                  value={license.id}
                  key={license.id}>
                  {license.name}
                </option>
              )}
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col md>
          <FloatingLabel label={t("block-type")}>
            <Form.Select
              onChange={(e) => blockKitStore?.setType(e.target.value)}
            >
              <option value={"all"} key={"all"}>all</option>
              {getBlockTypeConfigs()?.map((val) => {
                return (
                  <option value={val.blockType} key={val.blockType}>{val.blockType}</option>
                );
              })}
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col md>
          <FloatingLabel label={t("text-align")}>
            <Form.Select
              onChange={(e) => blockKitStore?.setTextAlignType(e.target.value)}
            >
              {textAlignTypesOptions?.map((val, idx) => {
                return (
                  <option value={val} key={idx}>{val}</option>
                );
              })}
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col md>
          <FloatingLabel label={t("layout")}>
            <Form.Select
              onChange={(e) => blockKitStore?.setLayoutType(e.target.value)}
            >
              <option value={"none"} key={"none"}>none</option>
              {layouts?.map((layout, index) =>
                <option value={layout.layoutType} key={index}>{layout.layoutType}</option>)}
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col md>
          <FloatingLabel label={(t("tag"))}>
            <Form.Select
              onChange={(e) => blockKitStore?.setTag(e.target.value)}
            >
              {tagsOptions?.map((val, idx) => {
                return (
                  <option value={val} key={idx}>
                    {val}
                  </option>
                );
              })}
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col md>
          <FloatingLabel label={t("sort-option")}>
            <Form.Select
              defaultValue={blockKitStore?.selectedSortingOption}
              onChange={(e) => blockKitStore?.setSortingOption(e.target.value as SortingOption)}
            >
              {Object.keys(SortingOption).map((sortingOption, idx) => (
                <option key={idx}>{t(`sort-option-${sortingOption}`)}</option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col md>
          <FloatingLabel label={t("dedicated-product")}>
            <Form.Select
              onChange={(e) => blockKitStore?.setProductOption(e.target.value as PolarProductTypes)}
            >
              <option value={"all"} key={"all"}>all</option>
              <option value={"none"} key={"none"}>none</option>
              {Object.values(PolarProductTypes).sort()?.map((productOption, idx) => (
                <option value={productOption} key={idx}>{productOption}</option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </Col>
      </Row>
      <Row>
        <Col md>
          <InputGroup>
            <Form.Control
              ref={inputSearchWord}
              onKeyDown={(e) => {
                if (e.key === "Enter")
                  onClickLoading();
              }}
              onChange={(e) => blockKitStore?.setSearchWord(e.target.value)}
              placeholder={t("search-description")}
            />
            <Button
              disabled={isLoading}
              onClick={() => onClickLoading()}
            >
              {t("button-search")}
            </Button>
          </InputGroup>
        </Col>
        <Col md>
          {!isLoading && (
            <Form.Label>
              {`${t("search-result")}: ${blockKitStore?.blocks.length}`}
            </Form.Label>
          )}
        </Col>
      </Row>
    </Container>
  )
}));

export default BlockFilterBox;
